import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { allocateGroup } from "@/lib/eligibility";
import { api, errMsg } from "@/lib/api";
import { getUtm, trackThen } from "@/lib/analytics";
import { hasConsent, metaIds, metaEvent, newEventId } from "@/lib/tracking";
import { StepProgram } from "@/components/register/StepProgram";
import { Logo } from "@/components/site/Header";
import { StepPlayer } from "@/components/register/StepPlayer";
import { StepParent } from "@/components/register/StepParent";
import { StepMedical } from "@/components/register/StepMedical";
import { StepConsent } from "@/components/register/StepConsent";
import { StepReview } from "@/components/register/StepReview";

const MAX_PLAYERS = 6;
const DRAFT_KEY = "epl_reg_draft";
const emptyMedical = { conditions: "", medications: "", injuries: "", asthma: false, asthma_details: "", severe_allergies: "", additional: "" };
const emptyPlayer = () => ({
  first_name: "", last_name: "", date_of_birth: "", gender: "", position: "", tshirt_size: "",
  medical: { ...emptyMedical },
  consent: { photo_social: null, photo_ads: null, guardian_confirmed: false, medical_treatment_authorised: false, terms_accepted: false, privacy_accepted: false },
});
const emptyParent = { full_name: "", email: "", mobile: "", emergency_name: "", emergency_phone: "", emergency_relationship: "", heard_about: "" };
const REQ = "Required";

const validate = (step, p, parent, group, slug) => {
  const e = {};
  if (step === 0 && !slug) e.program = "Select a programme";
  if (step === 1) {
    ["first_name", "last_name", "date_of_birth", "gender", "tshirt_size"].forEach((k) => { if (!String(p[k]).trim()) e[k] = REQ; });
    if (p.date_of_birth && p.gender && !group) e.date_of_birth = "Not eligible for any group";
  }
  if (step === 2) {
    Object.keys(emptyParent).filter((k) => k !== "heard_about").forEach((k) => { if (parent[k].trim().length < 2) e[k] = REQ; });
    if (parent.email && !/^\S+@\S+\.\S+$/.test(parent.email)) e.email = "Enter a valid email";
    ["mobile", "emergency_phone"].forEach((k) => { if (parent[k] && parent[k].replace(/\D/g, "").length < 8) e[k] = "Enter a valid phone number"; });
  }
  if (step === 4) {
    const c = p.consent;
    if (c.photo_social === null) e.photo_social = "Please choose Yes or No";
    if (c.photo_ads === null) e.photo_ads = "Please choose Yes or No";
    ["guardian_confirmed", "medical_treatment_authorised", "terms_accepted", "privacy_accepted"].forEach((k) => { if (!c[k]) e[k] = REQ; });
  }
  return e;
};

const Closed = ({ camp }) => (
  <div data-testid="registration-closed" className="text-center py-24">
    <h1 className="font-display font-bold text-4xl">{camp.sold_out ? "Sold out" : "Registrations closed"}</h1>
    <p className="mt-4 text-slate-400">{camp.sold_out ? "All places have been filled. There is no waitlist." : "Registrations for this programme have closed."}</p>
    <Link to="/" className="btn-primary mt-8">Back to home</Link>
  </div>
);

export default function Register() {
  const [camps, setCamps] = useState(null);
  const [slug, setSlug] = useState("");
  const [players, setPlayers] = useState([emptyPlayer()]);
  const [idx, setIdx] = useState(0);
  const [step, setStep] = useState(0);
  const [parent, setParentState] = useState(emptyParent);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(() => JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"));
  const [resumed, setResumed] = useState(false);
  const viewed = useRef(false);

  useEffect(() => {
    api.get("/camps").then((r) => {
      setCamps(r.data);
      const open = r.data.filter((c) => c.is_open);
      if (open.length === 1) setSlug(open[0].slug);
    }).catch(() => setCamps([]));
  }, []);
  const camp = camps?.find((c) => c.slug === slug) || camps?.[0];

  useEffect(() => {
    if (camp && !viewed.current) {
      viewed.current = true;
      metaEvent("ViewContent", { contentId: camp.slug, value: camp.price_cents / 100 });
    }
  }, [camp]);

  useEffect(() => {
    if (step < 2 || draft) return;
    // Medical details are deliberately never written to the device.
    const safe = players.map((x) => ({ ...x, medical: { ...emptyMedical } }));
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ slug, parent, players: safe, saved_at: Date.now() }));
  }, [players, parent, slug, step, draft]);

  const resume = () => {
    setSlug(draft.slug);
    setParentState(draft.parent);
    setPlayers(draft.players);
    setDraft(null);
    setResumed(true);
    setStep(draft.players[0]?.consent?.terms_accepted ? 5 : 1);
  };
  const discard = () => { localStorage.removeItem(DRAFT_KEY); setDraft(null); };

  const p = players[idx];
  const groupFor = (pl) => (pl.date_of_birth && pl.gender ? allocateGroup(+pl.date_of_birth.slice(0, 4), pl.gender, camp?.groups) : null);
  const update = (fn) => setPlayers((ps) => ps.map((x, i) => (i === idx ? fn(x) : x)));
  const set = (k, v) => update((x) => ({ ...x, [k]: v }));
  const setMedical = (k, v) => update((x) => ({ ...x, medical: { ...x.medical, [k]: v } }));
  const setConsent = (k, v) => update((x) => ({ ...x, consent: { ...x.consent, [k]: v } }));
  const setParent = (k, v) => setParentState((x) => ({ ...x, [k]: v }));
  const go = (s) => { setErrors({}); setStep(s); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const next = () => {
    const e = validate(step, p, parent, groupFor(p), slug);
    if (Object.keys(e).length) { setErrors(e); toast.error(e.program || "Please complete the highlighted fields"); return; }
    go(step === 1 && idx > 0 ? 3 : step + 1);
  };
  const back = () => go(step === 3 && idx > 0 ? 1 : step - 1);
  const addPlayer = () => { setPlayers((ps) => [...ps, emptyPlayer()]); setIdx(players.length); go(1); };
  const editPlayer = (i) => { setIdx(i); go(1); };
  const removePlayer = (i) => { setPlayers((ps) => ps.filter((_, j) => j !== i)); setIdx(0); };

  const pay = async () => {
    setBusy(true);
    try {
      const value = (camp.price_cents * players.length) / 100;
      const { data } = await api.post("/registrations/checkout", {
        camp_slug: camp.slug, parent, players, origin_url: window.location.origin, utm: getUtm(),
        meta: { consent: hasConsent(), ...metaIds(), event_source_url: window.location.href },
      });
      metaEvent("InitiateCheckout", { contentId: camp.slug, value, numItems: players.length }, newEventId());
      trackThen("begin_checkout", { value, currency: "AUD", num_players: players.length }, () => { window.location.href = data.checkout_url; });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (!camps) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin accent-text" /></div>;
  if (!camp) return <div className="min-h-screen flex items-center justify-center text-slate-400">No programmes are open for registration.</div>;
  const canAdd = players.length < Math.min(MAX_PLAYERS, camp.remaining ?? MAX_PLAYERS);

  return (
    <div data-testid="register-page" className="min-h-screen pb-32">
      <header className="max-w-2xl mx-auto px-5 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="register-home-link"><Logo size={36} /><span className="font-display font-bold text-sm hidden sm:block">Elite Performance Lab</span></Link>
        <span className="text-xs font-mono-epl text-slate-500">{camp.dates_label}</span>
      </header>
      <div className="max-w-2xl mx-auto px-5">
        {!camp.is_open ? <Closed camp={camp} /> : (
          <>
            <div className="h-1 rounded-full bg-white/10 mb-10 overflow-hidden"><div className="h-full bg-[var(--accent)] transition-[width] duration-500" style={{ width: `${((step + 1) / 6) * 100}%` }} /></div>
            {draft && step === 0 && (
              <div data-testid="resume-banner" className="mb-8 rounded-3xl border border-[var(--accent)]/30 bg-[var(--accent)]/[0.05] p-6">
                <p className="font-display font-bold text-lg">Continue where you left off?</p>
                <p className="mt-1 text-sm text-slate-400">We saved your unfinished registration on this device (medical details are never saved).</p>
                <div className="mt-4 flex gap-2">
                  <button data-testid="resume-registration" onClick={resume} className="btn-primary !py-3 !px-5">Resume</button>
                  <button data-testid="discard-registration" onClick={discard} className="btn-ghost !py-3 !px-5">Start over</button>
                </div>
              </div>
            )}
            {step === 0 && <StepProgram camps={camps} selected={slug} onSelect={setSlug} />}
            {step === 1 && <StepPlayer player={p} set={set} errors={errors} group={groupFor(p)} index={idx} />}
            {step === 2 && <StepParent parent={parent} set={setParent} errors={errors} />}
            {step === 3 && <StepMedical player={p} setMedical={setMedical} />}
            {step === 4 && <StepConsent player={p} setConsent={setConsent} parent={parent} setParent={setParent} errors={errors} showHeard={idx === 0} />}
            {step === 5 && <StepReview players={players} parent={parent} groupFor={groupFor} price={camp.price_cents} onEdit={editPlayer} onRemove={removePlayer} onAdd={addPlayer} canAdd={canAdd} resumed={resumed} campName={camp.name} perPlayer={camp.pay_per_player} />}
          </>
        )}
      </div>
      {camp.is_open && (
        <div className="fixed bottom-0 inset-x-0 bg-[#070a11]/90 backdrop-blur-xl border-t border-white/10 z-40">
          <div className="max-w-2xl mx-auto px-5 py-4 flex gap-3">
            {step > 0 && <button data-testid="register-back" onClick={back} className="btn-ghost !px-5"><ArrowLeft size={18} /></button>}
            {step < 5 ? (
              <button data-testid="register-next" onClick={next} className="btn-primary flex-1">Continue <ArrowRight size={18} /></button>
            ) : (
              <button data-testid="register-pay" onClick={pay} disabled={busy} className="btn-primary flex-1 disabled:opacity-60">
                {busy ? <Loader2 size={18} className="animate-spin" /> : "Continue to secure payment"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
